import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '../api/client';

const STATUSES = ['ACTIVE', 'PENDING', 'INACTIVE', 'SUSPENDED'] as const;
const ACCESS_LEVELS = [
  { value: 'TRADE', label: 'Trade — full access' },
  { value: 'VIEW_ONLY', label: 'View Only — read-only' },
] as const;

const empty = {
  email: '', fullName: '', phone: '', password: '',
  status: 'ACTIVE' as (typeof STATUSES)[number],
  accessLevel: 'TRADE' as (typeof ACCESS_LEVELS)[number]['value'],
};

export function CreateClientDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: async () => (await api.post('/clients', form)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      setForm(empty);
      onClose();
    },
  });

  const errMsg =
    (create.error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
    'Failed to create client';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Create New Client</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Full Name" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} fullWidth />
          <TextField label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} fullWidth />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField label="Phone (optional)" value={form.phone} onChange={(e) => set('phone', e.target.value)} fullWidth />
            <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value)} fullWidth>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>
          <TextField
            select
            label="Trade Access"
            value={form.accessLevel}
            onChange={(e) => set('accessLevel', e.target.value)}
            helperText="Trade clients can place/cancel/close orders. View-only clients can only view dashboards, orders & P&L."
            fullWidth
          >
            {ACCESS_LEVELS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Temporary Password"
            type="password"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            helperText="Minimum 8 characters — share securely with the client."
            fullWidth
          />
          {create.isError && <Alert severity="error">{errMsg}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={() => create.mutate()}
          disabled={create.isPending || !form.email || !form.fullName || form.password.length < 8}
        >
          {create.isPending ? 'Creating…' : 'Create Client'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
